import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import DynamicIconWrapper from '@components/common/DynamicIconWrapper/DynamicIconWrapper';

import { Icons } from '@icons/icons';

import { ARRAY_NODE_KEY } from '../../constants';

import { IFlatTreeNodeInterface } from '../../interface';
import FlatTreeChevron from '../FlatTreeChevron';
import SkipFlatNodeButton from '../SkipFlatNodeButton';

import styles from './FlatTreeNode.module.scss';

interface IFlatTreeNodeProps {
	node: IFlatTreeNodeInterface;
	skipNodeHandler: () => void;
	removeNodeHandler: () => void;
	enterEditMode: () => void;
	enterAddMode: () => void;
	toggleCollapse: () => void;
	copyNodeHandler: () => void;
	isCollapsed: boolean;
	isReadOnly?: boolean;
	brackets: {
		openedBracket?: string;
		closedBracket?: string;
	};
}

const FlatTreeNode = ({
	node,
	skipNodeHandler,
	removeNodeHandler,
	copyNodeHandler,
	enterAddMode,
	enterEditMode,
	toggleCollapse,
	isCollapsed,
	isReadOnly,
	brackets,
}: IFlatTreeNodeProps) => {
	const [isCopied, setIsCopied] = useState(false);
	const timerId = useRef<NodeJS.Timeout>();

	const nodeContentClasses = cn(styles.nodeContent, {
		[styles.readOnlyPadding]: isReadOnly && !node.nodeType,
	});

	const copyLabelClass = cn(styles.copyLabel, {
		[styles.copyLabelVisible]: isCopied,
	});

	const nodeControlClasses = cn(styles.nodeControl, {
		[styles.nodeControlPropertyPadding]: !isReadOnly && !node.nodeType,
		[styles.nodeControlDefaultPadding]: isReadOnly || !isReadOnly && node.nodeType,
	});

	useEffect(() => {
		if (isCopied && !timerId.current) {
			timerId.current = setTimeout(() => {
				setIsCopied(false);
			}, 500);
		}

		return () => {
			clearTimeout(timerId.current);
			timerId.current = undefined;
		};
	}, [isCopied]);

	const copyHandler = () => {
		copyNodeHandler();
		setIsCopied(true);
	};

	return (
		<div
			className={styles.node}
			data-testid={`node_${node.path}`}
		>
			{!isReadOnly && !node.nodeType &&
				<SkipFlatNodeButton
					isSkipped={!node.isChecked}
					onClick={skipNodeHandler}
				/>
			}
			{node.nodeType &&
				<FlatTreeChevron
					isCollapsed={isCollapsed}
					testName={node.path}
					toggleCollapse={toggleCollapse}
				/>
			}
			<div className={nodeContentClasses}>
				{node.key !== ARRAY_NODE_KEY &&
					<span
						className={styles.nodeKey}
						data-testid='node_key'
					>
						{node.key}:
					</span>
				}
				{node.value &&
					<span
						className={styles.nodeValue}
						data-testid='node_value'
					>
						{node.value}
					</span>
				}
				<div className={nodeControlClasses}>
					{brackets.openedBracket &&
						<span className={styles.nodeBracket}>
							{brackets.openedBracket}
						</span>
					}
					{!isReadOnly
						? node.nodeType
							? <>
								{node.description &&
									<DynamicIconWrapper
										icon={Icons.info}
										tooltipLabel={node.description}
									/>
								}
								<span className={styles.copyWrapper}>
									<DynamicIconWrapper
										icon={Icons.copy}
										onClick={copyHandler}
										testName={`_${node.path}_copy`}
										tooltipLabel='Copy parameter'
									/>
									<span className={copyLabelClass}>copied</span>
								</span>
								<DynamicIconWrapper
									icon={Icons.plus}
									onClick={enterAddMode}
									testName={`_${node.path}_add`}
									tooltipLabel='Add parameter'
								/>
							</>
							: <>
								<DynamicIconWrapper
									icon={Icons.editIcon}
									onClick={enterEditMode}
									testName={`_${node.path}_edit`}
									tooltipLabel='Edit parameter'
								/>
								<DynamicIconWrapper
									icon={Icons.delete}
									onClick={removeNodeHandler}
									testName={`_${node.path}_remove`}
									tooltipLabel='Remove parameter'
								/>
							</>
						: null
					}
					{brackets.closedBracket &&
						<span className={styles.nodeBracket}>
							{brackets.closedBracket}
						</span>
					}
				</div>
			</div>
		</div>
	);
};

export default FlatTreeNode;
